/**
 * UC-24 sibling: TriggerRandomEvent — probabilistic narrative events during tick.
 */
import type { RandomEvent } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';

export interface TriggerRandomEventInput {
  eventActive: boolean;
  compliance: number;
  threat: number;
  loanCount: number;
  /** Primary gate roll 0–1 (default 0.01 = 1% tick chance when not provided) */
  gateRoll?: number;
  /** Secondary pick roll 0–1 */
  pickRoll?: number;
  events: RandomEvent[];
}

export interface RandomEventStatAdjustments {
  budget?: number;
  loanCount?: number;
}

export interface TriggerRandomEventOutput {
  triggered: boolean;
  event?: RandomEvent;
  statAdjustments?: RandomEventStatAdjustments;
  playAlertSfx?: boolean;
}

const GATE_THRESHOLD = 0.02;

const EVENT_STAT_ADJUSTMENTS: Record<string, RandomEventStatAdjustments> = {
  fine: { loanCount: 1 },
  boom: { budget: 2000 },
  audit: { budget: -1000 },
};

export interface TriggerRandomEventDependencies {
  logger?: IAppLogger;
}

export class TriggerRandomEventUseCase {
  constructor(private readonly deps: TriggerRandomEventDependencies = {}) {}

  execute(input: TriggerRandomEventInput): TriggerRandomEventOutput {
    if (input.eventActive) {
      return { triggered: false };
    }

    const gateRoll = input.gateRoll ?? Math.random();
    if (gateRoll >= GATE_THRESHOLD) {
      return { triggered: false };
    }

    const pickRoll = input.pickRoll ?? Math.random();
    const { compliance, threat, loanCount, events } = input;

    let event: RandomEvent | undefined;

    if (compliance < 50 && pickRoll < 0.5) {
      event = events.find((e) => e.id === 'audit');
    } else if (threat > 30 && pickRoll < 0.5) {
      event = events.find((e) => e.id === 'hack');
    } else if (compliance > 80 && pickRoll < 0.3) {
      event = events.find((e) => e.id === 'boom');
    } else if (loanCount > 3 && pickRoll < 0.4) {
      event = events.find((e) => e.id === 'fine');
    }

    if (!event) {
      return { triggered: false };
    }

    this.deps.logger?.log('debug', 'random_event.triggered', { eventId: event.id });

    return {
      triggered: true,
      event,
      statAdjustments: EVENT_STAT_ADJUSTMENTS[event.id],
      playAlertSfx: true,
    };
  }
}